import {createContext, use} from 'react'
import {
    isInternalOTAssetKey,
    normaliseString,
    OTAssetKeys,
    type OTAssetRow,
    OTAssetSpecialKeys,
} from '../../../contexts/types/ot-types'
import {
    AssetStatusToLabelMap,
    OTAssetLifecycleStatusEnum,
} from '../../../../../store/state/ot-assets/types/ot-asset-lifecycle-status'
import type {GuidType} from '../../../../../values/generic-type-definitions'
import {
    COLUMN_MAPPING_DEFAULT_VALUE,
    type ColumnMappingView,
    type ColumnsMapping,
} from '../../../types/asset-upload-types'
import {CaseInsensitiveSet} from '../../../../../utils/collectionUtils/case-insensitive-set'
import type {NetworkAsset} from '@/api/network-assets/types/network-assets.types'
import {type OTLocation} from '../../../contexts/types/ot-inventory-response'

export enum Step {
    ASSET_UPLOAD_METHOD = 'ASSET_UPLOAD_METHOD',
    LIVE_ASSET_DETECTION = 'LIVE_ASSET_DETECTION',
    SPREADSHEET_UPLOAD = 'SPREADSHEET_UPLOAD',
    COLUMN_MAPPING = 'COLUMN_MAPPING',
    DATA_REVIEW = 'DATA_REVIEW',
    SUCCESS_NOTIFICATION = 'SUCCESS_NOTIFICATION',
}

// Re-export for backward compatibility
export type {ColumnsMapping, ColumnMappingView} from '../../../types/asset-upload-types'
export type OTAssetRowWithCustomFields = {
    [K in OTAssetKeys]?: string
} & {
    [OTAssetSpecialKeys.NO]: string
    'Custom Fields': string
    [OTAssetSpecialKeys.VESSEL_IMO]?: string
    [OTAssetSpecialKeys.VESSEL_CODE]?: string
}

export {COLUMN_MAPPING_DEFAULT_VALUE}

const mapAssetStatusLabelToEnum = (assetStatusLabel?: string) => {
    if (!assetStatusLabel) {
        return undefined
    }
    const pair = Object.entries(AssetStatusToLabelMap).find(
        ([, label]) => normaliseString(label) === normaliseString(assetStatusLabel),
    )
    if (pair) {
        return pair[0]
    }

    return undefined
}

function getLocationIdentifier(location: OTLocation): [OTAssetSpecialKeys, string] {
    if (location.code !== '') {
        return [OTAssetSpecialKeys.VESSEL_CODE, location.code]
    }

    if (location.customerCode !== '') {
        return [OTAssetSpecialKeys.VESSEL_IMO, location.customerCode]
    }

    throw new Error('no valid identifier for location: ' + JSON.stringify(location))
}

export const transformOTAssets = (
    assets: OTAssetRow[],
    location: OTLocation,
    columnsMapping: ColumnsMapping,
): OTAssetRowWithCustomFields[] => {
    const transformedJson = assets.map((asset, index) => {
        const x: OTAssetRowWithCustomFields = {
            [OTAssetSpecialKeys.NO]: (index + 1).toString(),
            [OTAssetKeys.NAME]: resolve(asset, OTAssetKeys.NAME),
            [OTAssetKeys.TYPE]: resolve(asset, OTAssetKeys.TYPE),
            [OTAssetKeys.VESSEL_SYSTEM]: resolve(asset, OTAssetKeys.VESSEL_SYSTEM),
            [OTAssetKeys.ZONE]: resolve(asset, OTAssetKeys.ZONE),
            [OTAssetKeys.BRAND]: resolve(asset, OTAssetKeys.BRAND),
            [OTAssetKeys.MODEL]: resolve(asset, OTAssetKeys.MODEL),
            [OTAssetKeys.DESCRIPTION]: resolve(asset, OTAssetKeys.DESCRIPTION),
            [OTAssetKeys.PHYSICAL_INTERFACES]: resolve(asset, OTAssetKeys.PHYSICAL_INTERFACES),
            [OTAssetKeys.COMMUNICATION_PROTOCOL]: resolve(
                asset,
                OTAssetKeys.COMMUNICATION_PROTOCOL,
            ),
            [OTAssetKeys.SOFTWARE_TYPE]: resolve(asset, OTAssetKeys.SOFTWARE_TYPE),
            [OTAssetKeys.SOFTWARE_VERSION]: resolve(asset, OTAssetKeys.SOFTWARE_VERSION),
            [OTAssetKeys.STATUS]: mapAssetStatusLabelToEnum(
                resolve(asset, OTAssetKeys.STATUS) ?? OTAssetLifecycleStatusEnum.ACTIVE,
            ),
            [OTAssetKeys.IP_ADDRESS]: resolve(asset, OTAssetKeys.IP_ADDRESS),
            [OTAssetKeys.MAC_ADDRESS]: resolve(asset, OTAssetKeys.MAC_ADDRESS),
            [OTAssetSpecialKeys.CUSTOM_FIELDS]: '',
        }

        const [colName, colValue] = getLocationIdentifier(location)
        x[colName] = colValue

        const extraFields = Object.entries(asset)
            .filter(([key]) => !columnsMapping[key as OTAssetKeys])
            .filter(([key]) => !isInternalOTAssetKey(key))
            .map(([name, value]: [string, unknown]) => ({
                name,
                value: String(value).trim(),
            }))
            .filter((field) => field.value.trim() !== '')

        x[OTAssetSpecialKeys.CUSTOM_FIELDS] =
            extraFields.length > 0 ? JSON.stringify(extraFields) : ''

        return x
    })

    return transformedJson

    function resolve(asset: OTAssetRow, key: OTAssetKeys) {
        if (!columnsMapping[key]) {
            return undefined
        }
        return asset[key]?.trim()
    }
}

export const AssetUploadContext = createContext<{
    step: Step
    changeStep: (step: Step) => void
    // Assets that are meant to be used as the final data for the upload
    assets: OTAssetRow[]
    // Raw, unmodified assets from excel sheets
    userRawAssets: OTAssetRow[]
    // Raw, unmodified column list from excel sheets
    userRawColumns: CaseInsensitiveSet
    // Mapping of the template columns to the user's column names
    columnsMapping: ColumnsMapping
    // AI suggested mapping of the template columns to the user's column names
    aiColumnsMapping?: ColumnsMapping
    columnMappingView: ColumnMappingView
    rawFile: File | null
    setUserRawAssets: (assets: OTAssetRow[]) => void
    setAssets: (assets: OTAssetRow[]) => void
    setColumnsMapping: (mapping: ColumnsMapping) => void
    setAiColumnsMapping: (mapping?: ColumnsMapping) => void
    setColumnMappingView: (view: ColumnMappingView) => void
    setRawFile: (file: File | null) => void
    fileId: GuidType
    setFileId: (fileId: GuidType) => void
    location: OTLocation | undefined
    // List of remaining columns after deletions.
    derivedUserColumns: CaseInsensitiveSet
    // List of columns deleted in column mapping step
    deletedColumns: string[]
    setDeletedColumns: (columns: string[]) => void
    resetState: () => void
    // a string that we can use to decide if the file is unique
    // ideally a hash of it.
    fileIdentifier: string | null
    setFileIdentifier: (identifier: string | null) => void
    // Live asset detection selections
    selectedLiveAssets: NetworkAsset[]
    setSelectedLiveAssets: (assets: NetworkAsset[]) => void
    importMode: 'bulk' | 'live' | undefined
    setImportMode: (mode: 'bulk' | 'live' | undefined) => void
}>({
    step: Step.ASSET_UPLOAD_METHOD,
    changeStep: () => {},
    assets: [],
    userRawAssets: [],
    userRawColumns: new CaseInsensitiveSet(),
    columnsMapping: COLUMN_MAPPING_DEFAULT_VALUE,
    aiColumnsMapping: undefined,
    columnMappingView: 'all',
    rawFile: null,
    setUserRawAssets: () => {},
    setAssets: () => {},
    setColumnsMapping: () => {},
    setAiColumnsMapping: () => {},
    setColumnMappingView: () => {},
    setRawFile: () => {},
    fileId: '',
    setFileId: () => {},
    location: undefined,
    derivedUserColumns: new CaseInsensitiveSet(),
    deletedColumns: [],
    setDeletedColumns: () => {},
    resetState: () => {},
    fileIdentifier: null,
    setFileIdentifier: () => {},
    selectedLiveAssets: [],
    setSelectedLiveAssets: () => {},
    importMode: undefined,
    setImportMode: () => {},
})

export function useAssetUploadContext() {
    return use(AssetUploadContext)
}
